package com.xtream.data_kmp.db.dao

import androidx.collection.LongSparseArray
import androidx.paging.PagingSource
import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.RoomRawQuery
import androidx.room3.coroutines.createFlow
import androidx.room3.paging.PagingSourceDaoReturnTypeConverter
import androidx.room3.util.appendPlaceholders
import androidx.room3.util.getColumnIndex
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.room3.util.recursiveFetchLongSparseArray
import androidx.sqlite.SQLiteConnection
import androidx.sqlite.SQLiteStatement
import com.xtream.data_kmp.db.tables.LiveStreamOrderEntity
import com.xtream.data_kmp.dto.LiveCategory
import com.xtream.data_kmp.dto.LiveStream
import com.xtream.data_kmp.dto.LiveStreamWithSortOrder
import kotlin.Boolean
import kotlin.Double
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlin.text.StringBuilder
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class LiveStreamsDao_Impl(
  __db: RoomDatabase,
) : LiveStreamsDao() {
  private val __db: RoomDatabase

  private val __insertAdapterOfLiveStream: EntityInsertAdapter<LiveStream>

  private val __deleteAdapterOfLiveStream: EntityDeleteOrUpdateAdapter<LiveStream>

  private val __pagingSourceDaoReturnTypeConverter: PagingSourceDaoReturnTypeConverter =
      PagingSourceDaoReturnTypeConverter()
  init {
    this.__db = __db
    this.__insertAdapterOfLiveStream = object : EntityInsertAdapter<LiveStream>() {
      protected override fun createQuery(): String = "INSERT OR REPLACE INTO `liveStreamsTable` (`streamId`,`added`,`categoryId`,`customSid`,`directSource`,`isAdult`,`name`,`num`,`streamIcon`,`streamType`,`epgChannelId`,`tvArchive`,`tvArchiveDuration`) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: LiveStream) {
        statement.bindLong(1, entity.streamId.toLong())
        statement.bindText(2, entity.added)
        statement.bindLong(3, entity.categoryId.toLong())
        val _tmpCustomSid: String? = entity.customSid
        if (_tmpCustomSid == null) {
          statement.bindNull(4)
        } else {
          statement.bindText(4, _tmpCustomSid)
        }
        val _tmpDirectSource: String? = entity.directSource
        if (_tmpDirectSource == null) {
          statement.bindNull(5)
        } else {
          statement.bindText(5, _tmpDirectSource)
        }
        statement.bindLong(6, entity.isAdult.toLong())
        statement.bindText(7, entity.name)
        val _tmpNum: Int? = entity.num
        if (_tmpNum == null) {
          statement.bindNull(8)
        } else {
          statement.bindLong(8, _tmpNum.toLong())
        }
        val _tmpStreamIcon: String? = entity.streamIcon
        if (_tmpStreamIcon == null) {
          statement.bindNull(9)
        } else {
          statement.bindText(9, _tmpStreamIcon)
        }
        statement.bindText(10, entity.streamType)
        val _tmpEpgChannelId: String? = entity.epgChannelId
        if (_tmpEpgChannelId == null) {
          statement.bindNull(11)
        } else {
          statement.bindText(11, _tmpEpgChannelId)
        }
        val _tmpTvArchive: Int? = entity.tvArchive
        if (_tmpTvArchive == null) {
          statement.bindNull(12)
        } else {
          statement.bindLong(12, _tmpTvArchive.toLong())
        }
        val _tmpTvArchiveDuration: Int? = entity.tvArchiveDuration
        if (_tmpTvArchiveDuration == null) {
          statement.bindNull(13)
        } else {
          statement.bindLong(13, _tmpTvArchiveDuration.toLong())
        }
      }
    }
    this.__deleteAdapterOfLiveStream = object : EntityDeleteOrUpdateAdapter<LiveStream>() {
      protected override fun createQuery(): String = "DELETE FROM `liveStreamsTable` WHERE `streamId` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: LiveStream) {
        statement.bindLong(1, entity.streamId.toLong())
      }
    }
  }

  public override suspend fun insert(item: LiveStream): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfLiveStream.insert(_connection, item)
  }

  public override suspend fun insert(items: List<LiveStream>): Unit = performSuspending(__db, false, true) { _connection ->
    __insertAdapterOfLiveStream.insert(_connection, items)
  }

  public override suspend fun delete(item: LiveStream): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfLiveStream.handle(_connection, item)
  }

  public override suspend fun delete(items: List<LiveStream>): Unit = performSuspending(__db, false, true) { _connection ->
    __deleteAdapterOfLiveStream.handleMultiple(_connection, items)
  }

  public override fun allAsFlow(): Flow<List<LiveStream>> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY num ASC
        |    
        """.trimMargin()
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun allAsFlow(
    categoryId: Int?,
    nameQuery: String?,
    limit: Int?,
    offset: Int?,
  ): Flow<List<LiveStream>> {
    val _sql: String = "SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR liveStreamsTable.categoryId = ?) AND (? IS NULL OR liveStreamsTable.name LIKE '%' || ? || '%') ORDER BY num ASC LIMIT ? OFFSET ?"
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        _argIndex = 4
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        _argIndex = 5
        if (limit == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, limit.toLong())
        }
        _argIndex = 6
        if (offset == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, offset.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun allAsFlow(categoryId: Int?, nameQuery: String?): Flow<List<LiveStream>> {
    val _sql: String = "SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR liveStreamsTable.categoryId = ?) AND (? IS NULL OR liveStreamsTable.name LIKE '%' || ? || '%') ORDER BY num ASC"
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        _argIndex = 4
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun allAsPagingSource(categoryId: Int?, nameQuery: String?): PagingSource<Int, LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR liveStreamsTable.categoryId = ?) AND (? IS NULL OR liveStreamsTable.name LIKE '%' || ? || '%') ORDER BY num ASC"
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql) { _stmt ->
      var _argIndex: Int = 1
      if (categoryId == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindLong(_argIndex, categoryId.toLong())
      }
      _argIndex = 2
      if (categoryId == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindLong(_argIndex, categoryId.toLong())
      }
      _argIndex = 3
      if (nameQuery == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindText(_argIndex, nameQuery)
      }
      _argIndex = 4
      if (nameQuery == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindText(_argIndex, nameQuery)
      }
    }
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override fun allAsPagingSource(categoryIds: List<Int>?, nameQuery: String?): PagingSource<Int, LiveStream> {
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND liveStreamsTable.categoryId IN (")
    val _inputSize: Int = if (categoryIds == null) 1 else categoryIds.size
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(") AND (")
    _stringBuilder.append("?")
    _stringBuilder.append(" IS NULL OR liveStreamsTable.name LIKE '%' || ")
    _stringBuilder.append("?")
    _stringBuilder.append(" || '%') ORDER BY num ASC")
    val _sql: String = _stringBuilder.toString()
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql) { _stmt ->
      var _argIndex: Int = 1
      if (categoryIds == null) {
        _stmt.bindNull(_argIndex)
      } else {
        for (_item: Int in categoryIds) {
          _stmt.bindLong(_argIndex, _item.toLong())
          _argIndex++
        }
      }
      _argIndex = 1 + _inputSize
      if (nameQuery == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindText(_argIndex, nameQuery)
      }
      _argIndex = 2 + _inputSize
      if (nameQuery == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindText(_argIndex, nameQuery)
      }
    }
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override fun allAsPagingSource(): PagingSource<Int, LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL ORDER BY num ASC"
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql)
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override suspend fun hasEntity(id: Int): Boolean {
    val _sql: String = "SELECT EXISTS (SELECT 1 FROM liveStreamsTable WHERE streamId = ?)"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, id.toLong())
        val _result: Boolean
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp != 0
        } else {
          _result = false
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun hasEntityAsFlow(id: Int?): Flow<Boolean> {
    val _sql: String = "SELECT EXISTS (SELECT 1 FROM liveStreamsTable WHERE streamId = ?)"
    return createFlow(__db, false, arrayOf("liveStreamsTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (id == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, id.toLong())
        }
        val _result: Boolean
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp != 0
        } else {
          _result = false
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getSize(): Long? {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getSize(categoryId: Int?): Long? {
    val _sql: String = "SELECT COUNT(*) FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR ? = -1000 OR liveStreamsTable.categoryId = ?)"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun recentlyAdded(): List<LiveStream> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY added DESC LIMIT 10
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun recentlyAddedFlow(limit: Int): Flow<List<LiveStream>> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY added DESC LIMIT ?
        |    
        """.trimMargin()
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getRecentlyAddedSize(): Flow<Long> {
    val _sql: String = """
        |
        |        SELECT COUNT(*) FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY added DESC LIMIT 10
        |    
        """.trimMargin()
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Long
        if (_stmt.step()) {
          val _tmp: Long
          _tmp = _stmt.getLong(0)
          _result = _tmp
        } else {
          _result = 0L
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getRecentlyAdded(limit: Int): PagingSource<Int, LiveStream> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY num ASC
        |        LIMIT ?
        |    
        """.trimMargin()
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql) { _stmt ->
      var _argIndex: Int = 1
      _stmt.bindLong(_argIndex, limit.toLong())
    }
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override fun getRecentlyAddedFlow(limit: Int): Flow<List<LiveStream>> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY added DESC
        |        LIMIT ?
        |    
        """.trimMargin()
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getRecommendations(categoryId: Int?, limit: Int): PagingSource<Int, LiveStream> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        AND (? IS NULL OR liveStreamsTable.categoryId = ?)
        |        ORDER BY added DESC
        |        LIMIT ?
        |    
        """.trimMargin()
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql) { _stmt ->
      var _argIndex: Int = 1
      if (categoryId == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindLong(_argIndex, categoryId.toLong())
      }
      _argIndex = 2
      if (categoryId == null) {
        _stmt.bindNull(_argIndex)
      } else {
        _stmt.bindLong(_argIndex, categoryId.toLong())
      }
      _argIndex = 3
      _stmt.bindLong(_argIndex, limit.toLong())
    }
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override suspend fun getFavoriteStreams(): List<LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.*, liveFavoritesTable.sortOrder AS  'num' FROM liveStreamsTable INNER JOIN liveFavoritesTable ON liveStreamsTable.streamId = liveFavoritesTable.streamId ORDER BY liveFavoritesTable.sortOrder ASC"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getFavoriteStreamsFlow(limit: Int?): Flow<List<LiveStream>> {
    val _sql: String = "SELECT liveStreamsTable.*, liveFavoritesTable.sortOrder AS  'num' FROM liveStreamsTable INNER JOIN liveFavoritesTable ON liveStreamsTable.streamId = liveFavoritesTable.streamId ORDER BY liveFavoritesTable.sortOrder ASC LIMIT ?"
    return createFlow(__db, false, arrayOf("liveStreamsTable", "liveFavoritesTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (limit == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, limit.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getPagedFavoriteStreams(): PagingSource<Int, LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.*, liveFavoritesTable.sortOrder AS  'num' FROM liveStreamsTable INNER JOIN liveFavoritesTable ON liveStreamsTable.streamId = liveFavoritesTable.streamId ORDER BY liveFavoritesTable.sortOrder ASC"
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql)
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "liveFavoritesTable"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override fun getPagedWatchedStreams(): PagingSource<Int, LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.*  FROM liveStreamsTable INNER JOIN watchedLiveStreamsTable ON liveStreamsTable.streamId = watchedLiveStreamsTable.streamId  ORDER BY watchedLiveStreamsTable.added DESC "
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql)
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "watchedLiveStreamsTable"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
          val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
          val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
          val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
          val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
          val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
          val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
          val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
          val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
          val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
          val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            val _tmpStreamId: Int
            _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
            val _tmpAdded: String
            _tmpAdded = _stmt.getText(_columnIndexOfAdded)
            val _tmpCategoryId: Int
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            val _tmpCustomSid: String?
            if (_stmt.isNull(_columnIndexOfCustomSid)) {
              _tmpCustomSid = null
            } else {
              _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
            }
            val _tmpDirectSource: String?
            if (_stmt.isNull(_columnIndexOfDirectSource)) {
              _tmpDirectSource = null
            } else {
              _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
            }
            val _tmpIsAdult: Int
            _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
            val _tmpName: String
            _tmpName = _stmt.getText(_columnIndexOfName)
            val _tmpNum: Int?
            if (_stmt.isNull(_columnIndexOfNum)) {
              _tmpNum = null
            } else {
              _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
            }
            val _tmpStreamIcon: String?
            if (_stmt.isNull(_columnIndexOfStreamIcon)) {
              _tmpStreamIcon = null
            } else {
              _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
            }
            val _tmpStreamType: String
            _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
            val _tmpEpgChannelId: String?
            if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
              _tmpEpgChannelId = null
            } else {
              _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
            }
            val _tmpTvArchive: Int?
            if (_stmt.isNull(_columnIndexOfTvArchive)) {
              _tmpTvArchive = null
            } else {
              _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
            }
            val _tmpTvArchiveDuration: Int?
            if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
              _tmpTvArchiveDuration = null
            } else {
              _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
            }
            _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override fun getWatchedStreamsFlow(limit: Int?): Flow<List<LiveStream>> {
    val _sql: String = "SELECT liveStreamsTable.*  FROM liveStreamsTable INNER JOIN watchedLiveStreamsTable ON liveStreamsTable.streamId = watchedLiveStreamsTable.streamId  ORDER BY watchedLiveStreamsTable.added DESC LIMIT ?"
    return createFlow(__db, false, arrayOf("liveStreamsTable", "watchedLiveStreamsTable")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (limit == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, limit.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getSizeAsFlow(categoryId: Int?): Flow<Long?> {
    val _sql: String = "SELECT COUNT(*) FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR liveStreamsTable.categoryId = ?)"
    return createFlow(__db, false, arrayOf("liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        val _result: Long?
        if (_stmt.step()) {
          val _tmp: Long?
          if (_stmt.isNull(0)) {
            _tmp = null
          } else {
            _tmp = _stmt.getLong(0)
          }
          _result = _tmp
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getStreamById(id: Int?): LiveStream? {
    val _sql: String = "SELECT * FROM liveStreamsTable WHERE streamId = ? LIMIT 1"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (id == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, id.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: LiveStream?
        if (_stmt.step()) {
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _result = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun all(
    categoryId: Int?,
    nameQuery: String?,
    limit: Int?,
    offset: Int?,
  ): List<LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.* FROM liveStreamsTable LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE' WHERE h.categoryId IS NULL AND (? IS NULL OR liveStreamsTable.categoryId = ?) AND (? IS NULL OR liveStreamsTable.name LIKE '%' || ? || '%') ORDER BY num ASC LIMIT ? OFFSET ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        _argIndex = 4
        if (nameQuery == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindText(_argIndex, nameQuery)
        }
        _argIndex = 5
        if (limit == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, limit.toLong())
        }
        _argIndex = 6
        if (offset == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, offset.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getByOffset(categoryId: Int?, offset: Int): LiveStream? {
    val _sql: String = """
        |
        |    SELECT liveStreamsTable.* FROM liveStreamsTable
        |    LEFT JOIN live_stream_order o ON liveStreamsTable.streamId = o.streamId
        |    LEFT JOIN hidden_categories h ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |    WHERE h.categoryId IS NULL
        |    AND (? = -1000 OR liveStreamsTable.categoryId = ?)
        |    ORDER BY
        |        COALESCE(o.position, liveStreamsTable.num),
        |        CASE WHEN o.position IS NOT NULL THEN 0 ELSE 1 END
        |    LIMIT 1 OFFSET ?
        |""".trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 3
        _stmt.bindLong(_argIndex, offset.toLong())
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: LiveStream?
        if (_stmt.step()) {
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _result = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun categoriesWithStreams(): PagingSource<Int, LiveStreamsDao.LiveCategoryWithStreams> {
    val _sql: String = """
        |
        |        SELECT liveCategoriesTable.* FROM liveCategoriesTable
        |        LEFT JOIN hidden_categories h
        |            ON liveCategoriesTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY 
        |            liveCategoriesTable.created_at
        |    
        """.trimMargin()
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql)
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable", "liveCategoriesTable", "hidden_categories"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStreamsDao.LiveCategoryWithStreams>>(__db, true, true) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
          val _columnIndexOfCategoryName: Int = getColumnIndexOrThrow(_stmt, "categoryName")
          val _columnIndexOfParentId: Int = getColumnIndexOrThrow(_stmt, "parentId")
          val _columnIndexOfCreatedAt: Int = getColumnIndexOrThrow(_stmt, "created_at")
          val _collectionStream: LongSparseArray<MutableList<LiveStream>> = LongSparseArray<MutableList<LiveStream>>()
          while (_stmt.step()) {
            val _tmpKey: Long?
            if (_stmt.isNull(_columnIndexOfCategoryId)) {
              _tmpKey = null
            } else {
              _tmpKey = _stmt.getLong(_columnIndexOfCategoryId)
            }
            if (_tmpKey != null) {
              if (!_collectionStream.containsKey(_tmpKey)) {
                _collectionStream.put(_tmpKey, mutableListOf())
              }
            }
          }
          _stmt.reset()
          __fetchRelationshipliveStreamsTableAscomXtreamDataKmpDtoLiveStream(_connection, _collectionStream)
          val _result: MutableList<LiveStreamsDao.LiveCategoryWithStreams> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStreamsDao.LiveCategoryWithStreams
            val _tmpCategory: LiveCategory
            val _tmpCategoryId: Int?
            if (_stmt.isNull(_columnIndexOfCategoryId)) {
              _tmpCategoryId = null
            } else {
              _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
            }
            val _tmpCategoryName: String?
            if (_stmt.isNull(_columnIndexOfCategoryName)) {
              _tmpCategoryName = null
            } else {
              _tmpCategoryName = _stmt.getText(_columnIndexOfCategoryName)
            }
            val _tmpParentId: Int?
            if (_stmt.isNull(_columnIndexOfParentId)) {
              _tmpParentId = null
            } else {
              _tmpParentId = _stmt.getLong(_columnIndexOfParentId).toInt()
            }
            val _tmpCreatedAt: Long
            _tmpCreatedAt = _stmt.getLong(_columnIndexOfCreatedAt)
            _tmpCategory = LiveCategory(_tmpCategoryId,_tmpCategoryName,_tmpParentId,_tmpCreatedAt)
            val _tmpStreamCollection: MutableList<LiveStream>
            val _tmpKey_1: Long?
            if (_stmt.isNull(_columnIndexOfCategoryId)) {
              _tmpKey_1 = null
            } else {
              _tmpKey_1 = _stmt.getLong(_columnIndexOfCategoryId)
            }
            if (_tmpKey_1 != null) {
              _tmpStreamCollection = checkNotNull(_collectionStream.get(_tmpKey_1))
            } else {
              _tmpStreamCollection = mutableListOf()
            }
            _item = LiveStreamsDao.LiveCategoryWithStreams(_tmpCategory,_tmpStreamCollection)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override suspend fun getRecentlyAddedList(limit: Int): List<LiveStream> {
    val _sql: String = """
        |
        |        SELECT liveStreamsTable.* FROM liveStreamsTable
        |        LEFT JOIN hidden_categories h
        |            ON liveStreamsTable.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        ORDER BY added DESC
        |        LIMIT ?
        |    
        """.trimMargin()
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindLong(_argIndex, limit.toLong())
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getWatchedStreams(limit: Int?): List<LiveStream> {
    val _sql: String = "SELECT liveStreamsTable.*  FROM liveStreamsTable INNER JOIN watchedLiveStreamsTable ON liveStreamsTable.streamId = watchedLiveStreamsTable.streamId  ORDER BY watchedLiveStreamsTable.added DESC LIMIT ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (limit == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, limit.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _result: MutableList<LiveStream> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun getOrdered(categoryId: Int?): Flow<List<LiveStreamWithSortOrder>> {
    val _sql: String = """
        |
        |        SELECT l.*
        |        FROM liveStreamsTable l
        |        LEFT JOIN live_stream_order o
        |            ON l.streamId = o.streamId
        |        LEFT JOIN hidden_categories h
        |            ON l.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        AND (? IS NULL OR l.categoryId = ?)
        |        ORDER BY
        |            COALESCE(o.position, l.num),
        |            CASE WHEN o.position IS NOT NULL THEN 0 ELSE 1 END
        |    
        """.trimMargin()
    return createFlow(__db, true, arrayOf("live_stream_order", "liveStreamsTable", "hidden_categories")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _collectionOrder: LongSparseArray<LiveStreamOrderEntity?> = LongSparseArray<LiveStreamOrderEntity?>()
        while (_stmt.step()) {
          val _tmpKey: Long
          _tmpKey = _stmt.getLong(_columnIndexOfStreamId)
          _collectionOrder.put(_tmpKey, null)
        }
        _stmt.reset()
        __fetchRelationshipliveStreamOrderAscomXtreamDataKmpDbTablesLiveStreamOrderEntity(_connection, _collectionOrder)
        val _result: MutableList<LiveStreamWithSortOrder> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStreamWithSortOrder
          val _tmpLiveStream: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _tmpLiveStream = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          val _tmpOrder: LiveStreamOrderEntity?
          val _tmpKey_1: Long
          _tmpKey_1 = _stmt.getLong(_columnIndexOfStreamId)
          _tmpOrder = _collectionOrder.get(_tmpKey_1)
          _item = LiveStreamWithSortOrder(_tmpLiveStream,_tmpOrder)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getOrderedTvChannelsList(categoryId: Int?): List<LiveStreamWithSortOrder> {
    val _sql: String = """
        |
        |        SELECT l.*
        |        FROM liveStreamsTable l
        |        LEFT JOIN live_stream_order o
        |            ON l.streamId = o.streamId
        |        LEFT JOIN hidden_categories h
        |            ON l.categoryId = h.categoryId AND h.type = 'LIVE'
        |        WHERE h.categoryId IS NULL
        |        AND (? IS NULL OR l.categoryId = ?)
        |        ORDER BY
        |            COALESCE(o.position, l.num),
        |            CASE WHEN o.position IS NOT NULL THEN 0 ELSE 1 END
        |    
        """.trimMargin()
    return performSuspending(__db, true, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        _argIndex = 2
        if (categoryId == null) {
          _stmt.bindNull(_argIndex)
        } else {
          _stmt.bindLong(_argIndex, categoryId.toLong())
        }
        val _columnIndexOfStreamId: Int = getColumnIndexOrThrow(_stmt, "streamId")
        val _columnIndexOfAdded: Int = getColumnIndexOrThrow(_stmt, "added")
        val _columnIndexOfCategoryId: Int = getColumnIndexOrThrow(_stmt, "categoryId")
        val _columnIndexOfCustomSid: Int = getColumnIndexOrThrow(_stmt, "customSid")
        val _columnIndexOfDirectSource: Int = getColumnIndexOrThrow(_stmt, "directSource")
        val _columnIndexOfIsAdult: Int = getColumnIndexOrThrow(_stmt, "isAdult")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfNum: Int = getColumnIndexOrThrow(_stmt, "num")
        val _columnIndexOfStreamIcon: Int = getColumnIndexOrThrow(_stmt, "streamIcon")
        val _columnIndexOfStreamType: Int = getColumnIndexOrThrow(_stmt, "streamType")
        val _columnIndexOfEpgChannelId: Int = getColumnIndexOrThrow(_stmt, "epgChannelId")
        val _columnIndexOfTvArchive: Int = getColumnIndexOrThrow(_stmt, "tvArchive")
        val _columnIndexOfTvArchiveDuration: Int = getColumnIndexOrThrow(_stmt, "tvArchiveDuration")
        val _collectionOrder: LongSparseArray<LiveStreamOrderEntity?> = LongSparseArray<LiveStreamOrderEntity?>()
        while (_stmt.step()) {
          val _tmpKey: Long
          _tmpKey = _stmt.getLong(_columnIndexOfStreamId)
          _collectionOrder.put(_tmpKey, null)
        }
        _stmt.reset()
        __fetchRelationshipliveStreamOrderAscomXtreamDataKmpDbTablesLiveStreamOrderEntity(_connection, _collectionOrder)
        val _result: MutableList<LiveStreamWithSortOrder> = mutableListOf()
        while (_stmt.step()) {
          val _item: LiveStreamWithSortOrder
          val _tmpLiveStream: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _tmpLiveStream = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          val _tmpOrder: LiveStreamOrderEntity?
          val _tmpKey_1: Long
          _tmpKey_1 = _stmt.getLong(_columnIndexOfStreamId)
          _tmpOrder = _collectionOrder.get(_tmpKey_1)
          _item = LiveStreamWithSortOrder(_tmpLiveStream,_tmpOrder)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun deleteAll() {
    val _sql: String = "DELETE FROM liveStreamsTable"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun allAsPagingSourceRaw(query: RoomRawQuery): PagingSource<Int, LiveStream> {
    val _sql: String = query.sql
    val _rawQuery: RoomRawQuery = RoomRawQuery(_sql) { _stmt ->
      query.getBindingFunction().invoke(_stmt)
    }
    return __pagingSourceDaoReturnTypeConverter.convert(__db, arrayOf("liveStreamsTable"), _rawQuery) { _converterQuery ->
      performSuspending<MutableList<LiveStream>>(__db, true, false) { _connection ->
        val _stmt: SQLiteStatement = _connection.prepare(_converterQuery.sql)
        try {
          _converterQuery.getBindingFunction().invoke(_stmt)
          val _result: MutableList<LiveStream> = mutableListOf()
          while (_stmt.step()) {
            val _item: LiveStream
            _item = __entityStatementConverter_comXtreamDataKmpDtoLiveStream(_stmt)
            _result.add(_item)
          }
          _result
        } finally {
          _stmt.close()
        }
      }
    }
  }

  public override suspend fun indexOf(rawQuery: RoomRawQuery): Int? {
    val _sql: String = rawQuery.sql
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        rawQuery.getBindingFunction().invoke(_stmt)
        val _result: Int?
        if (_stmt.step()) {
          if (_stmt.isNull(0)) {
            _result = null
          } else {
            _result = _stmt.getLong(0).toInt()
          }
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  private suspend fun __fetchRelationshipliveStreamsTableAscomXtreamDataKmpDtoLiveStream(_connection: SQLiteConnection, _map: LongSparseArray<MutableList<LiveStream>>) {
    if (_map.isEmpty()) {
      return
    }
    if (_map.size() > 999) {
      recursiveFetchLongSparseArray(_map, true) { _tmpMap ->
        __fetchRelationshipliveStreamsTableAscomXtreamDataKmpDtoLiveStream(_connection, _tmpMap)
      }
      return
    }
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT `streamId`, `added`, `categoryId`, `customSid`, `directSource`, `isAdult`, `name`, `num`, `streamIcon`, `streamType`, `epgChannelId`, `tvArchive`, `tvArchiveDuration` FROM `liveStreamsTable`")
    _stringBuilder.append(" WHERE `categoryId` IN (")
    val _inputSize: Int = _map.size()
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(")")
    val _sql: String = _stringBuilder.toString()
    val _stmt: SQLiteStatement = _connection.prepare(_sql)
    var _argIndex: Int = 1
    for (i in 0 until _map.size()) {
      val _item: Long = _map.keyAt(i)
      _stmt.bindLong(_argIndex, _item)
      _argIndex++
    }
    try {
      val _itemKeyIndex_0: Int = getColumnIndex(_stmt, "categoryId")
      if (_itemKeyIndex_0 == -1) {
        return
      }
      val _columnIndexOfStreamId: Int = 0
      val _columnIndexOfAdded: Int = 1
      val _columnIndexOfCategoryId: Int = 2
      val _columnIndexOfCustomSid: Int = 3
      val _columnIndexOfDirectSource: Int = 4
      val _columnIndexOfIsAdult: Int = 5
      val _columnIndexOfName: Int = 6
      val _columnIndexOfNum: Int = 7
      val _columnIndexOfStreamIcon: Int = 8
      val _columnIndexOfStreamType: Int = 9
      val _columnIndexOfEpgChannelId: Int = 10
      val _columnIndexOfTvArchive: Int = 11
      val _columnIndexOfTvArchiveDuration: Int = 12
      while (_stmt.step()) {
        val _tmpKey: Long
        _tmpKey = _stmt.getLong(_itemKeyIndex_0)
        val _tmpRelation: MutableList<LiveStream>? = _map.get(_tmpKey)
        if (_tmpRelation != null) {
          val _item_1: LiveStream
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpAdded: String
          _tmpAdded = _stmt.getText(_columnIndexOfAdded)
          val _tmpCategoryId: Int
          _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          val _tmpCustomSid: String?
          if (_stmt.isNull(_columnIndexOfCustomSid)) {
            _tmpCustomSid = null
          } else {
            _tmpCustomSid = _stmt.getText(_columnIndexOfCustomSid)
          }
          val _tmpDirectSource: String?
          if (_stmt.isNull(_columnIndexOfDirectSource)) {
            _tmpDirectSource = null
          } else {
            _tmpDirectSource = _stmt.getText(_columnIndexOfDirectSource)
          }
          val _tmpIsAdult: Int
          _tmpIsAdult = _stmt.getLong(_columnIndexOfIsAdult).toInt()
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpNum: Int?
          if (_stmt.isNull(_columnIndexOfNum)) {
            _tmpNum = null
          } else {
            _tmpNum = _stmt.getLong(_columnIndexOfNum).toInt()
          }
          val _tmpStreamIcon: String?
          if (_stmt.isNull(_columnIndexOfStreamIcon)) {
            _tmpStreamIcon = null
          } else {
            _tmpStreamIcon = _stmt.getText(_columnIndexOfStreamIcon)
          }
          val _tmpStreamType: String
          _tmpStreamType = _stmt.getText(_columnIndexOfStreamType)
          val _tmpEpgChannelId: String?
          if (_stmt.isNull(_columnIndexOfEpgChannelId)) {
            _tmpEpgChannelId = null
          } else {
            _tmpEpgChannelId = _stmt.getText(_columnIndexOfEpgChannelId)
          }
          val _tmpTvArchive: Int?
          if (_stmt.isNull(_columnIndexOfTvArchive)) {
            _tmpTvArchive = null
          } else {
            _tmpTvArchive = _stmt.getLong(_columnIndexOfTvArchive).toInt()
          }
          val _tmpTvArchiveDuration: Int?
          if (_stmt.isNull(_columnIndexOfTvArchiveDuration)) {
            _tmpTvArchiveDuration = null
          } else {
            _tmpTvArchiveDuration = _stmt.getLong(_columnIndexOfTvArchiveDuration).toInt()
          }
          _item_1 = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
          _tmpRelation.add(_item_1)
        }
      }
    } finally {
      _stmt.close()
    }
  }

  private suspend fun __fetchRelationshipliveStreamOrderAscomXtreamDataKmpDbTablesLiveStreamOrderEntity(_connection: SQLiteConnection, _map: LongSparseArray<LiveStreamOrderEntity?>) {
    if (_map.isEmpty()) {
      return
    }
    if (_map.size() > 999) {
      recursiveFetchLongSparseArray(_map, false) { _tmpMap ->
        __fetchRelationshipliveStreamOrderAscomXtreamDataKmpDbTablesLiveStreamOrderEntity(_connection, _tmpMap)
      }
      return
    }
    val _stringBuilder: StringBuilder = StringBuilder()
    _stringBuilder.append("SELECT `id`, `streamId`, `position`, `categoryId`, `updatedAt` FROM `live_stream_order`")
    _stringBuilder.append(" WHERE `streamId` IN (")
    val _inputSize: Int = _map.size()
    appendPlaceholders(_stringBuilder, _inputSize)
    _stringBuilder.append(")")
    val _sql: String = _stringBuilder.toString()
    val _stmt: SQLiteStatement = _connection.prepare(_sql)
    var _argIndex: Int = 1
    for (i in 0 until _map.size()) {
      val _item: Long = _map.keyAt(i)
      _stmt.bindLong(_argIndex, _item)
      _argIndex++
    }
    try {
      val _itemKeyIndex_0: Int = getColumnIndex(_stmt, "streamId")
      if (_itemKeyIndex_0 == -1) {
        return
      }
      val _columnIndexOfId: Int = 0
      val _columnIndexOfStreamId: Int = 1
      val _columnIndexOfPosition: Int = 2
      val _columnIndexOfCategoryId: Int = 3
      val _columnIndexOfUpdatedAt: Int = 4
      while (_stmt.step()) {
        val _tmpKey: Long
        _tmpKey = _stmt.getLong(_itemKeyIndex_0)
        if (_map.containsKey(_tmpKey)) {
          val _item_1: LiveStreamOrderEntity?
          val _tmpId: Long
          _tmpId = _stmt.getLong(_columnIndexOfId)
          val _tmpStreamId: Int
          _tmpStreamId = _stmt.getLong(_columnIndexOfStreamId).toInt()
          val _tmpPosition: Double
          _tmpPosition = _stmt.getDouble(_columnIndexOfPosition)
          val _tmpCategoryId: Int?
          if (_stmt.isNull(_columnIndexOfCategoryId)) {
            _tmpCategoryId = null
          } else {
            _tmpCategoryId = _stmt.getLong(_columnIndexOfCategoryId).toInt()
          }
          val _tmpUpdatedAt: Long
          _tmpUpdatedAt = _stmt.getLong(_columnIndexOfUpdatedAt)
          _item_1 = LiveStreamOrderEntity(_tmpId,_tmpStreamId,_tmpPosition,_tmpCategoryId,_tmpUpdatedAt)
          _map.put(_tmpKey, _item_1)
        }
      }
    } finally {
      _stmt.close()
    }
  }

  private fun __entityStatementConverter_comXtreamDataKmpDtoLiveStream(statement: SQLiteStatement): LiveStream {
    val _entity: LiveStream
    val _columnIndexOfStreamId: Int = getColumnIndex(statement, "streamId")
    val _columnIndexOfAdded: Int = getColumnIndex(statement, "added")
    val _columnIndexOfCategoryId: Int = getColumnIndex(statement, "categoryId")
    val _columnIndexOfCustomSid: Int = getColumnIndex(statement, "customSid")
    val _columnIndexOfDirectSource: Int = getColumnIndex(statement, "directSource")
    val _columnIndexOfIsAdult: Int = getColumnIndex(statement, "isAdult")
    val _columnIndexOfName: Int = getColumnIndex(statement, "name")
    val _columnIndexOfNum: Int = getColumnIndex(statement, "num")
    val _columnIndexOfStreamIcon: Int = getColumnIndex(statement, "streamIcon")
    val _columnIndexOfStreamType: Int = getColumnIndex(statement, "streamType")
    val _columnIndexOfEpgChannelId: Int = getColumnIndex(statement, "epgChannelId")
    val _columnIndexOfTvArchive: Int = getColumnIndex(statement, "tvArchive")
    val _columnIndexOfTvArchiveDuration: Int = getColumnIndex(statement, "tvArchiveDuration")
    val _tmpStreamId: Int
    if (_columnIndexOfStreamId == -1) {
      _tmpStreamId = 0
    } else {
      _tmpStreamId = statement.getLong(_columnIndexOfStreamId).toInt()
    }
    val _tmpAdded: String
    if (_columnIndexOfAdded == -1) {
      error("Missing column 'added' for a NON-NULL value, column not found in result.")
    } else {
      _tmpAdded = statement.getText(_columnIndexOfAdded)
    }
    val _tmpCategoryId: Int
    if (_columnIndexOfCategoryId == -1) {
      _tmpCategoryId = 0
    } else {
      _tmpCategoryId = statement.getLong(_columnIndexOfCategoryId).toInt()
    }
    val _tmpCustomSid: String?
    if (_columnIndexOfCustomSid == -1) {
      _tmpCustomSid = null
    } else {
      if (statement.isNull(_columnIndexOfCustomSid)) {
        _tmpCustomSid = null
      } else {
        _tmpCustomSid = statement.getText(_columnIndexOfCustomSid)
      }
    }
    val _tmpDirectSource: String?
    if (_columnIndexOfDirectSource == -1) {
      _tmpDirectSource = null
    } else {
      if (statement.isNull(_columnIndexOfDirectSource)) {
        _tmpDirectSource = null
      } else {
        _tmpDirectSource = statement.getText(_columnIndexOfDirectSource)
      }
    }
    val _tmpIsAdult: Int
    if (_columnIndexOfIsAdult == -1) {
      _tmpIsAdult = 0
    } else {
      _tmpIsAdult = statement.getLong(_columnIndexOfIsAdult).toInt()
    }
    val _tmpName: String
    if (_columnIndexOfName == -1) {
      error("Missing column 'name' for a NON-NULL value, column not found in result.")
    } else {
      _tmpName = statement.getText(_columnIndexOfName)
    }
    val _tmpNum: Int?
    if (_columnIndexOfNum == -1) {
      _tmpNum = null
    } else {
      if (statement.isNull(_columnIndexOfNum)) {
        _tmpNum = null
      } else {
        _tmpNum = statement.getLong(_columnIndexOfNum).toInt()
      }
    }
    val _tmpStreamIcon: String?
    if (_columnIndexOfStreamIcon == -1) {
      _tmpStreamIcon = null
    } else {
      if (statement.isNull(_columnIndexOfStreamIcon)) {
        _tmpStreamIcon = null
      } else {
        _tmpStreamIcon = statement.getText(_columnIndexOfStreamIcon)
      }
    }
    val _tmpStreamType: String
    if (_columnIndexOfStreamType == -1) {
      error("Missing column 'streamType' for a NON-NULL value, column not found in result.")
    } else {
      _tmpStreamType = statement.getText(_columnIndexOfStreamType)
    }
    val _tmpEpgChannelId: String?
    if (_columnIndexOfEpgChannelId == -1) {
      _tmpEpgChannelId = null
    } else {
      if (statement.isNull(_columnIndexOfEpgChannelId)) {
        _tmpEpgChannelId = null
      } else {
        _tmpEpgChannelId = statement.getText(_columnIndexOfEpgChannelId)
      }
    }
    val _tmpTvArchive: Int?
    if (_columnIndexOfTvArchive == -1) {
      _tmpTvArchive = null
    } else {
      if (statement.isNull(_columnIndexOfTvArchive)) {
        _tmpTvArchive = null
      } else {
        _tmpTvArchive = statement.getLong(_columnIndexOfTvArchive).toInt()
      }
    }
    val _tmpTvArchiveDuration: Int?
    if (_columnIndexOfTvArchiveDuration == -1) {
      _tmpTvArchiveDuration = null
    } else {
      if (statement.isNull(_columnIndexOfTvArchiveDuration)) {
        _tmpTvArchiveDuration = null
      } else {
        _tmpTvArchiveDuration = statement.getLong(_columnIndexOfTvArchiveDuration).toInt()
      }
    }
    _entity = LiveStream(_tmpStreamId,_tmpAdded,_tmpCategoryId,_tmpCustomSid,_tmpDirectSource,_tmpIsAdult,_tmpName,_tmpNum,_tmpStreamIcon,_tmpStreamType,_tmpEpgChannelId,_tmpTvArchive,_tmpTvArchiveDuration)
    return _entity
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
